import {useAppStore} from '@cohort/wallet/hooks/stores/app';
import {useMerchantContext} from '@cohort/wallet/hooks/useMerchantContext';
import {useCallback} from 'react';
import {shallow} from 'zustand/shallow';

type UseShopify = {
  active: boolean;
  signInRedirect: () => void;
  signInCheckoutRedirect: (campaignSlug: string, queryParams: string) => void;
  spaceRedirect: () => void;
};

const useShopify = (): UseShopify => {
  const {integration, embedded} = useAppStore(
    store => ({
      integration: store.integration,
      embedded: store.embedded,
    }),
    shallow
  );
  const shopifyIntegration = integration?.type === 'shopify' ? integration : undefined;
  const merchant = useMerchantContext();

  const redirect = useCallback(
    (url: URL) => {
      if (embedded) {
        window.parent.location.href = url.href;
      } else {
        window.location.href = url.href;
      }
    },
    [embedded]
  );

  const signInRedirect = useCallback(() => {
    if (shopifyIntegration) {
      const signInUrl = new URL(
        shopifyIntegration.loginPath,
        `https://${shopifyIntegration.shopUrl}`
      );
      signInUrl.searchParams.append('return_url', shopifyIntegration.appPath);
      redirect(signInUrl);
    }
  }, [shopifyIntegration, redirect]);

  const signInCheckoutRedirect = useCallback(
    (campaignSlug: string, searchString: string) => {
      if (shopifyIntegration) {
        const signInUrl = new URL(
          shopifyIntegration.loginPath,
          `https://${shopifyIntegration.shopUrl}`
        );
        const checkoutPath = `/apps/cohort/${merchant.slug}/store/${campaignSlug}/checkout${searchString}`;
        signInUrl.searchParams.append('return_url', checkoutPath);
        redirect(signInUrl);
      }
    },
    [shopifyIntegration, merchant.slug, redirect]
  );

  const spaceRedirect = useCallback(() => {
    if (shopifyIntegration) {
      const spaceUrl = new URL(
        `/apps/cohort/${merchant.slug}/space`,
        `https://${shopifyIntegration.shopUrl}`
      );
      redirect(spaceUrl);
    }
  }, [shopifyIntegration, merchant.slug, redirect]);

  return {
    active: shopifyIntegration !== undefined,
    signInRedirect,
    spaceRedirect,
    signInCheckoutRedirect,
  };
};

export default useShopify;
